import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { logger } from "@/lib/logger";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PlusCircle, Save, Trash2, FileCheck, GripVertical } from "lucide-react";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  verticalListSortingStrategy, useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

function SortableItem({ id, value, index, onChange, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} className="flex gap-2 items-center">
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-[#083CA6] p-1 touch-none"
        aria-label="Arraste para reordenar"
        data-testid={`docs-drag-${index}`}
      >
        <GripVertical className="w-4 h-4" />
      </button>
      <Input
        value={value}
        onChange={(e) => onChange(index, e.target.value)}
        placeholder="Descrição do documento"
        data-testid={`docs-input-${index}`}
      />
      <Button
        size="sm"
        variant="outline"
        className="text-red-600 border-red-300"
        onClick={() => onRemove(index)}
        data-testid={`docs-remove-${index}`}
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );
}

export default function DocumentosObrigatoriosPage() {
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const load = useCallback(async () => {
    try {
      const r = await api.get("/config/documentos-obrigatorios");
      setItems(r.data.items || []);
    } catch (e) {
      logger.error("Falha ao carregar", e);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setAt = (i, v) => setItems((prev) => prev.map((x, idx) => idx === i ? v : x));
  const addNew = () => setItems((prev) => [...prev, ""]);
  const removeAt = (i) => setItems((prev) => prev.filter((_, idx) => idx !== i));

  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = Number(active.id.replace("item-", ""));
    const newIdx = Number(over.id.replace("item-", ""));
    setItems((prev) => arrayMove(prev, oldIdx, newIdx));
  };

  const salvar = async () => {
    const clean = items.map((s) => s.trim()).filter(Boolean);
    if (clean.length === 0) return toast.error("A lista não pode estar vazia");
    setSaving(true);
    try {
      const r = await api.put("/config/documentos-obrigatorios", { items: clean });
      setItems(r.data.items);
      toast.success("Documentos obrigatórios atualizados");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    } finally { setSaving(false); }
  };

  const ids = items.map((_, i) => `item-${i}`);

  return (
    <div className="space-y-6" data-testid="docs-obrigatorios-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-black text-[#083CA6] flex items-center gap-3">
            <FileCheck className="w-8 h-8" />Documentação Obrigatória
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Editar a lista de documentos exibida na seção "Documentos necessários" da página inicial
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={addNew} data-testid="btn-add-doc">
            <PlusCircle className="w-4 h-4 mr-2" />Adicionar item
          </Button>
          <Button onClick={salvar} disabled={saving} className="bg-[#00883A] hover:bg-[#006e2e]" data-testid="btn-salvar-docs">
            <Save className="w-4 h-4 mr-2" />{saving ? "Salvando..." : "Publicar no site"}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Itens ({items.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-slate-500 mb-3">
            Arraste pelo ícone ⋮⋮ para reordenar. As alterações aparecem no site após clicar em "Publicar no site".
          </p>
          {items.length === 0 ? (
            <div className="text-center text-slate-500 py-8">Nenhum item cadastrado.</div>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={ids} strategy={verticalListSortingStrategy}>
                <div className="space-y-2">
                  {items.map((v, i) => (
                    <SortableItem
                      key={`item-${i}`}
                      id={`item-${i}`}
                      index={i}
                      value={v}
                      onChange={setAt}
                      onRemove={removeAt}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
